// What the warehouse app's scans of an order mean, for the order view (app
// 0.8.0). Pure functions over GET wms/orders/{id}/scans.

// The step a scan was made at.
export const STAGE_WORDS = {
  pick: "Picking",
  pack: "Packing",
  verify: "Checking",
  load: "Loading",
  deliver: "Delivery",
  ship: "Shipping",
};

// Picking; checking (packing or a check); the driver (loading, delivery);
// shipping (labels logged leaving: the Scanner's log, the pickup check).
export const groupOf = (stage) =>
  stage === "pick"
    ? "pick"
    : stage === "load" || stage === "deliver"
      ? "load"
      : stage === "ship"
        ? "ship"
        : "check";

const COUNTED = ["match", "verified"];

// What a scan meant, in a few words.
export function resultText(row) {
  switch (row?.result) {
    case "match":
      return row.picked ? "Right item · picked 1" : "Right item";
    case "verified":
      return "Right item (picked before)";
    case "over":
      return "Right item, all counted already";
    case "label": {
      const what = row.carrier ? `${row.carrier} label` : "This order's label";
      return row.package?.no && row.package?.of
        ? `${what} · package ${row.package.no} of ${row.package.of}`
        : what;
    }
    case "order":
      return "This order's number";
    case "other_order":
      return `Label of another order${row.other_order ? ` (${row.other_order.order_number})` : ""}`;
    case "wrong_item":
      return `Wrong item${row.product ? `: ${row.product.name}` : ""}`;
    default:
      return "Code not in Bolt";
  }
}

// The rest of a scan's story: its note, and "scanned before" for a label
// logged twice at the same step.
export const scanExtras = (row) =>
  [row?.note || null, row?.duplicate ? "scanned before" : null].filter(Boolean);

export const resultTone = (result) =>
  ["match", "verified", "label"].includes(result)
    ? "success"
    : ["other_order", "wrong_item"].includes(result)
      ? "danger"
      : result === "order"
        ? "neutral"
        : "warning";

// Per order line: units counted at each step group, with the last scan's
// time and who. Lines come from the order (order_details), so lines nobody
// scanned show too.
export function lineScans(orderDetails, scans) {
  const byLine = new Map();
  [...(scans || [])].reverse().forEach((row) => {
    const id = row?.line?.order_detail_id;
    if (!id || !COUNTED.includes(row.result)) return;
    const group = groupOf(row.stage);
    const line = byLine.get(id) || {};
    const count = line[group] || { units: 0, at: null, by: null };
    line[group] = {
      units: count.units + Math.max(1, Number(row.quantity) || 0),
      at: row.at,
      by: row.by?.name ?? null,
    };
    byLine.set(id, line);
  });
  return (orderDetails || []).map((detail) => {
    const counts = byLine.get(detail?.id) || {};
    return {
      id: detail?.id,
      name: detail?.product_name ?? "-",
      sku: detail?.sku ?? null,
      ordered: Number(detail?.item_quantity) || 0,
      pick: counts.pick || null,
      check: counts.check || null,
      load: counts.load || null,
    };
  });
}

// "Nov 1, 22:31" in the browser's time.
export function timeText(value, locale = "en-CA") {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const day = date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
  });
  const time = date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${day}, ${time}`;
}

// "2 of 3" (a line's units, or labels), or the count alone when there's no total.
export const ofText = (count, total) =>
  total ? `${Math.min(count, total)} of ${total}` : String(count);
